import { useEffect, useMemo, useState } from 'react';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonPage,
  IonSearchbar,
  IonSpinner,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { getSupabase } from '../../lib/supabase.js';

// T-282 — DDT del preposto. I PDF stanno in storage privato (preposto fail-closed),
// quindi i dati arrivano dall'edge `preposto-portal` (service-role, scoped al claim):
// elenco DDT emessi verso la sua struttura + URL firmato del PDF.

interface Ddt {
  id: string;
  doc_number: number | null;
  doc_year: number | null;
  issued_at: string | null;
  pdf_url: string | null;
  line_count: number;
}

export default function PrepostoDdtPage(): JSX.Element {
  const sb = useMemo(() => getSupabase(), []);
  const [loading, setLoading] = useState(true);
  const [ddts, setDdts] = useState<Ddt[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return ddts;
    return ddts.filter((d) => {
      const txt = `ddt n. ${d.doc_number ?? ''}/${d.doc_year ?? ''} ${
        d.issued_at ? new Date(d.issued_at).toLocaleDateString('it-IT') : ''
      }`.toLowerCase();
      return txt.includes(s);
    });
  }, [ddts, q]);

  useEffect(() => {
    let mounted = true;
    void (async () => {
      const { data, error } = await sb.functions.invoke('preposto-portal');
      if (!mounted) return;
      if (error) setToast('Errore nel caricamento dei DDT.');
      else setDdts(((data as { ddts?: Ddt[] } | null)?.ddts) ?? []);
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [sb]);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/preposto" />
          </IonButtons>
          <IonTitle>DDT ricevuti</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><IonSpinner /></div>
        ) : ddts.length === 0 ? (
          <IonNote style={{ padding: '0 4px' }}>Nessun DDT emesso verso questa struttura.</IonNote>
        ) : (
          <>
            <IonSearchbar
              value={q}
              debounce={150}
              placeholder="Cerca per numero o data…"
              onIonInput={(e) => setQ(String(e.detail.value ?? ''))}
            />
            <IonList inset>
            {filtered.map((d) => (
              <IonItem key={d.id}>
                <IonLabel>
                  DDT n. {d.doc_number ?? '—'}/{d.doc_year ?? '—'}
                  <p>
                    {d.issued_at ? new Date(d.issued_at).toLocaleDateString('it-IT') : ''} · {d.line_count} righe
                  </p>
                </IonLabel>
                <IonButton
                  slot="end"
                  fill="outline"
                  size="small"
                  disabled={!d.pdf_url}
                  onClick={() => d.pdf_url && window.open(d.pdf_url, '_blank')}
                >
                  {d.pdf_url ? 'Apri PDF' : 'PDF n/d'}
                </IonButton>
              </IonItem>
            ))}
            </IonList>
          </>
        )}
        <IonToast isOpen={toast !== null} message={toast ?? ''} duration={2500} onDidDismiss={() => setToast(null)} />
      </IonContent>
    </IonPage>
  );
}
